import { Component, OnChanges, OnInit, SimpleChanges, inject, input, output, signal } from '@angular/core';

import { Observable } from 'rxjs';

import { AttachmentsResult, DocumentsAttachmentsModal, DocumentsAttachmentsModalInput } from '@camelot/files-extended';
import { InputUploadValue } from '@camelot/form-model';
import { CamDocumentsService } from '@camelot/services';
import { LayoutFullPanelComponent } from '@camelot/ui';
import { CamAbstractComponent, ModalState } from '@camelot/utils';

import {
  ConversationDocumentsKey,
  ConversationDocumentsPools,
  buildConversationSectionsHelper,
  conversationDocumentsRecord,
} from '../../helpers/documents-sections.helper';
import { CamTranslationCommunication } from '../../translation.service';

/** tout ce qui concerne les pièces jointes d'un message, regroupé en un seul input */
export type ConversationAttachments = {
  /** documents déjà liés à la conversation, à la tâche et au projet */
  documentsIds: ConversationDocumentsPools;
  /** ouverture de la modale pilotée depuis l'extérieur */
  open$?: Observable<void>;
  /** documents pré-sélectionnés par un modèle de message */
  preSet: string[];
  /** pièces jointes actuellement retenues */
  value: InputUploadValue[];
};

@Component({
  imports: [DocumentsAttachmentsModal, LayoutFullPanelComponent],
  selector: 'cam-communication-conversation-attachments',
  styleUrl: './conversation-attachments.component.scss',
  templateUrl: './conversation-attachments.component.html',
})
export class ConversationAttachmentsComponent extends CamAbstractComponent implements OnChanges, OnInit {
  attachments = input.required<ConversationAttachments>();

  attachmentsChanged = output<InputUploadValue[]>();

  public conversationAttachmentsModal = new ModalState<
    DocumentsAttachmentsModalInput<ConversationDocumentsKey>,
    AttachmentsResult
  >();

  public sectionsHelper = signal(buildConversationSectionsHelper({}));

  private readonly _documentsService = inject(CamDocumentsService);
  private _appliedPreSet: string[] = [];

  constructor() {
    super();
    CamTranslationCommunication.getInstance();
  }

  ngOnInit(): void {
    const open$ = this.attachments().open$;
    if (open$) {
      this._registerSubscription(
        open$.subscribe({
          next: () => this.open(),
        })
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['attachments']) {
      return;
    }

    // comparaison par contenu : l'objet est reconstruit à chaque détection de changement
    const preSet = this.attachments().preSet;
    if (preSet.length === 0 || this._isAppliedPreSet(preSet)) {
      return;
    }

    this._appliedPreSet = preSet;
    this._updateAttachments(
      this._documentsService.getDocuments(preSet)?.map(doc => ({
        id: doc.id,
        name: doc.name,
        url: doc.url,
      })) ?? []
    );
  }

  public open() {
    const documentsIds = this.attachments().documentsIds;
    this.sectionsHelper.set(buildConversationSectionsHelper(documentsIds));
    this.conversationAttachmentsModal.asked({
      documentsIds: conversationDocumentsRecord(documentsIds),
      initialAttachments: this.attachments().value,
    });
  }

  public onConfirmed(result: AttachmentsResult) {
    this._updateAttachments(result.result);
  }

  private _updateAttachments(attachments: InputUploadValue[]) {
    this.attachmentsChanged.emit(attachments);
  }

  private _isAppliedPreSet(preSet: string[]) {
    return (
      preSet.length === this._appliedPreSet.length && preSet.every((id, index) => id === this._appliedPreSet[index])
    );
  }
}
